import React, { useMemo, useEffect } from "react";
import "../styles/ItemModal.css";

const priceToNumber = (val) => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  return parseFloat(String(val).replace(",", ".").trim()) || 0;
};

export default function ItemModal({
  selectedItem,
  selectedSize,
  selectedIngredients,
  selectedExtra,            // single-select extra id (string)
  ingredientsMap,
  extrasMap,                // id -> extra doc
  setSelectedSize,
  setSelectedExtra,         // setter for radio
  toggleIngredient,
  handleAddToCart,
  closeModal,
}) {
  // ---- Hooks at top (no early returns before hooks) ----
  const sortedSizes = useMemo(() => {
    if (!selectedItem?.sizes?.length) return [];
    const list = selectedItem.sizes
      .map((label) => ({
        label,
        price: priceToNumber(selectedItem.prices?.[label]),
      }))
      .filter((s) => Number.isFinite(s.price));
    list.sort((a, b) => a.price - b.price);
    return list;
  }, [selectedItem]);

  const baseSize = sortedSizes[0]?.label;
  const basePrice = sortedSizes[0]?.price ?? 0;

  // Ensure default selected size = cheapest
  useEffect(() => {
    if (!selectedItem || !sortedSizes.length) return;
    if (!selectedSize || !sortedSizes.some((s) => s.label === selectedSize)) {
      setSelectedSize(sortedSizes[0].label);
    }
  }, [selectedItem, sortedSizes, selectedSize, setSelectedSize]);

  // ---- Safe early return AFTER hooks
  if (!selectedItem) return null;

  return (
    <div className="modal-overlay" onClick={closeModal} role="presentation">
      <div
        className="modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-modal-title"
        aria-describedby="item-modal-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-button" onClick={closeModal} aria-label="Zamknij okno">
          &times;
        </button>

        <div className="modal-content">
          <h2 id="item-modal-title" className="modal-title">{selectedItem.name?.pl}</h2>

          {selectedItem.imageUrl && (
            <img className="modal-img" src={selectedItem.imageUrl} alt={selectedItem.name?.pl} />
          )}

          {selectedItem.description?.pl && (
            <p id="item-modal-desc" className="modal-description">{selectedItem.description?.pl}</p>
          )}

          {/* Sizes (radio, cheapest first) */}
          {sortedSizes.length > 0 && (
            <div className="size-collapse">
              {sortedSizes.map(({ label, price }) => {
                const priceDiff = price - basePrice;
                return (
                  <label key={label} className="size-option">
                    <input
                      type="radio"
                      name="modal-size"
                      value={label}
                      checked={selectedSize === label}
                      onChange={() => setSelectedSize(label)}
                    />
                    <div className="size-info">
                      <span>{label}</span>
                      {priceDiff > 0 && (
                        <span className="price-diff">+{priceDiff.toFixed(2)} zł</span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Extras (single-select radio) */}
          {selectedItem.extras?.length > 0 && (
            <div className="modal-ingredients">
              <h4>Wybierz dodatek (opcjonalnie)</h4>
              <ul>
                {/* Clear selection */}
                <li key="__none">
                  <label className="ingredient-checkbox">
                    <input
                      type="radio"
                      name="modal-extra"
                      value=""
                      checked={!selectedExtra}
                      onChange={() => setSelectedExtra("")}
                    />
                    <span className="ingredient-name">Bez dodatku</span>
                  </label>
                </li>

                {selectedItem.extras.map((id) => {
                  const ex = extrasMap?.[id];
                  if (!ex) return null;
                  const exPrice = priceToNumber(ex._priceNumber ?? ex.price).toFixed(2);
                  return (
                    <li key={id}>
                      <label className="ingredient-checkbox">
                        <input
                          type="radio"
                          name="modal-extra"
                          value={id}
                          checked={selectedExtra === id}
                          onChange={() => {
                            setSelectedExtra(id);
                            // (Optional) quick debug — uncomment if you want an alert
                            // alert(`Wybrany dodatek: ${ex.name?.pl} (+${exPrice} zł)`);
                          }}
                        />
                        <span className="ingredient-name">{ex.name?.pl}</span>
                        <span className="ingredient-price">+{exPrice} zł</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Ingredients (multi-select) */}
          {selectedItem.ingredientIds?.length > 0 && (
            <div className="modal-ingredients">
              <h4>Dodatkowe składniki</h4>
              <ul>
                {selectedItem.ingredientIds.map((id) => {
                  const ing = ingredientsMap?.[id];
                  if (!ing) return null;
                  const ingPrice = priceToNumber(ing.price).toFixed(2);
                  return (
                    <li key={id}>
                      <label className="ingredient-checkbox">
                        <input
                          type="checkbox"
                          checked={Array.isArray(selectedIngredients) && selectedIngredients.includes(id)}
                          onChange={() => toggleIngredient(id)}
                        />
                        <span className="ingredient-name">{ing.name?.pl}</span>
                        <span className="ingredient-price">+{ingPrice} zł</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Confirm */}
          <button
            className="confirm-add-button"
            onClick={() =>
              handleAddToCart(
                selectedItem,
                selectedSize || baseSize,
                selectedIngredients,   // multi-select stays the same
                selectedExtra          // single extra id ("" or id)
              )
            }
          >
            Dodaj do koszyka
          </button>
        </div>
      </div>
    </div>
  );
}


//Workign itemodal page